// Media Accreditation Types
// Matches Supabase schema for media personnel and accreditation management

import type { LocationLabelSource } from "@/utils/locationDisplay";

/**
 * Stade d'un match tel qu'embarqué dans les requêtes média.
 * `location` vient de l'embed PostgREST `locations!fk_stadiums_location`
 * (alias `commune:communes`) et alimente formatLocationLabel().
 */
export interface MediaMatchStadium {
    name: string;
    name_ar?: string | null;
    location_id?: string | null;
    location?: LocationLabelSource | null;
}

// ============================================================================
// ENUMS - Aligned with database enums
// ============================================================================

export enum MediaOrgType {
    NEWSPAPER = "newspaper",
    TV = "tv",
    RADIO = "radio",
    ONLINE = "online",
    PHOTO_AGENCY = "photo_agency",
    OTHER = "other",
}

export enum MediaPersonnelType {
    JOURNALIST = "journalist",
    PHOTOGRAPHER = "photographer",
    CAMERAMAN = "cameraman",
    RADIO = "radio",
    TV_CREW = "tv_crew",
    EDITOR = "editor",
    PRODUCER = "producer",
}

export enum AccreditationRequestType {
    SINGLE_MATCH = "single_match",
    SEASON_PASS = "season_pass",
    TOURNAMENT = "tournament",
}

export enum AccreditationStatus {
    DRAFT = "draft",
    SUBMITTED = "submitted",
    UNDER_REVIEW = "under_review",
    APPROVED = "approved",
    REJECTED = "rejected",
    REVOKED = "revoked",
    EXPIRED = "expired",
}

export enum ZoneType {
    MAIN_COURANTE = "main_courante",
    PRESS_TRIBUNE = "press_tribune",
    MIXED_ZONE = "mixed_zone",
    PITCHSIDE = "pitchside",
    PHOTO_ZONE = "photo_zone",
    PRESS_CONFERENCE = "press_conference",
    MEDIA_CENTER = "media_center",
}

/**
 * Self-declared identity of a media_personnel row (GAP-1).
 * `professional` holds an ONJSA press card; `club_correspondent` covers a
 * single affiliated club and holds a President-stamped attestation instead.
 * Not federation-controlled — self-registering journalists and org owners
 * may set this freely on insert/update.
 */
export type PersonnelKind = "professional" | "club_correspondent";

export enum ScanResultType {
    VALID = "valid",
    INVALID = "invalid",
    EXPIRED = "expired",
    REVOKED = "revoked",
    WRONG_MATCH = "wrong_match",
    CAPACITY_EXCEEDED = "capacity_exceeded",
    WRONG_ZONE = "wrong_zone",
}

// ============================================================================
// INTERFACES - Database table types
// ============================================================================

export interface MediaOrganization {
    id: string;
    name: string;
    name_ar?: string | null;
    org_type: MediaOrgType;
    account_kind?: "press" | "club";
    team_id?: string | null;
    team?: {
        id: string;
        name: string;
        name_ar?: string | null;
        logo_url?: string | null;
    } | null;
    country?: string | null;
    website?: string | null;
    logo_url?: string | null;
    address?: string | null;
    address_ar?: string | null;
    contact_email?: string | null;
    contact_phone?: string | null;
    notes?: string | null;
    // Agrément (LIRF agreement) tracking
    agreement_url?: string | null;
    /** Declared by the organization at signup (media app); frozen once approved. */
    agreement_number?: string | null;
    agreement_issuer?: string | null;
    is_approved: boolean;
    approved_by?: string | null;
    approved_at?: string | null;
    // Participation (federation authorization to participate)
    is_active?: boolean | null;
    // Org-account login (owner)
    owner_user_id?: string | null;
    // LIRF import provenance
    lirf_status?: string | null;
    /** Soft delete by the league: set = hidden going forward, kept on past matches. */
    deleted_at?: string | null;
    created_at: string;
    updated_at?: string | null;
    // Joined / computed data
    journalist_count?: number;
}

export interface MediaPersonnel {
    id: string;
    user_id: string | null;
    profile_id?: string | null;
    organization_id: string | null;
    media_type: MediaPersonnelType;
    press_card_number: string | null;
    press_card_url: string | null;
    press_card_expiry?: string | null;
    press_card_issued_at?: string | null;
    photo_url: string | null;
    is_verified: boolean;
    verified_by: string | null;
    verified_at: string | null;
    rejection_reason: string | null;
    rejected_at: string | null;
    // Federation participation authorization (distinct from is_verified)
    participation_suspended?: boolean | null;
    // Self-declared identity (GAP-1) — not federation-controlled
    personnel_kind: PersonnelKind;
    affiliated_team_id?: string | null;
    // Typed documents (GAP-2): ONJSA card (professional) / club attestation (club_correspondent)
    onjsa_card_url?: string | null;
    club_attestation_url?: string | null;
    // Flexible verification documents — identity doc + agreement (up to 3 docs, each type once)
    id_card_url?: string | null;
    agreement_url?: string | null;
    // Denormalized bio for account-less (imported) personnel — profile_id is NULL
    first_name?: string | null;
    last_name?: string | null;
    first_name_ar?: string | null;
    last_name_ar?: string | null;
    date_of_birth?: string | null;
    place_of_birth?: string | null;
    nationality?: string | null;
    email?: string | null;
    phone?: string | null;
    secondary_phone?: string | null;
    role_title?: string | null;
    lirf_status?: string | null;
    /** Soft delete by the league: set = hidden going forward, kept on past matches. */
    deleted_at?: string | null;
    created_at: string;
    updated_at?: string | null;
    // First mobile connection timestamp — null until the journalist logs in once
    last_login_at?: string | null;
    // Joined data
    profile?: {
        id: string;
        full_name: string;
        email?: string;
        phone?: string;
        avatar_url?: string | null;
    };
    organization?: MediaOrganization;
    affiliated_team?: {
        id: string;
        name: string;
        name_ar?: string | null;
    } | null;
}

export interface AccreditationRequest {
    id: string;
    media_personnel_id: string;
    league_id: string;
    season?: string | null;
    match_id: string | null;
    /** Parent per-match demand (org-only model); null for standalone requests. */
    demand_id?: string | null;
    request_type: AccreditationRequestType;
    status: AccreditationStatus;
    requested_zones: ZoneType[];
    approved_zones: ZoneType[];
    coverage_purpose?: string | null;
    valid_from: string | null;
    valid_until: string | null;
    reviewed_by: string | null;
    reviewed_at: string | null;
    rejection_reason: string | null;
    organization_name?: string | null;
    created_at: string;
    updated_at?: string | null;
    // Badge issuance tracking
    badge_serial?: string | null;
    badge_template_id?: string | null;
    badge_issued_at?: string | null;
    badge_pdf_path?: string | null;
    badge_downloaded_at?: string | null;
    badge_download_count?: number | null;
    /**
     * Reviewer profile, embedded via accreditation_requests_reviewed_by_fkey
     * (reviewed_by references profiles(id)). Null when undecided or the
     * profile row is gone.
     */
    reviewer?: { id: string; full_name: string | null } | null;
    // Joined data
    media_personnel?: MediaPersonnel;
    league?: {
        id: string;
        name: string;
        name_ar?: string | null;
    };
    match?: {
        id: string;
        /** matches.status (match_status enum) — decides "played" for deleted holders. */
        status?: string | null;
        match_date: string;
        /** TIME column — recombined with match_date by demandKickoffIso(). */
        match_time?: string | null;
        game_day?: number | null;
        home_team?: { name: string; name_ar?: string | null } | null;
        away_team?: { name: string; name_ar?: string | null } | null;
        stadium?: MediaMatchStadium | null;
    };
    /**
     * QR tokens minted for this accreditation (RLS-scoped: stays empty for
     * roles without read access to accreditation_qr_tokens, which simply
     * degrades the badge cell to the plain "issued" state).
     */
    qr_tokens?: AccreditationQrTokenSummary[];
}

/** Minimal QR-token projection: enough to tell an active QR from a stale one. */
export interface AccreditationQrTokenSummary {
    expires_at: string;
    is_revoked?: boolean | null;
}

// ============================================================================
// ACCREDITATION DEMANDS (org-submitted, per-match group of journalists)
// ============================================================================

export type DemandStatus =
    | "pending"
    | "accepted"
    | "rejected"
    | "partially_accepted"
    | "cancelled";

export interface DemandRequestRow {
    id: string;
    status: AccreditationStatus;
    rejection_reason: string | null;
    badge_issued_at: string | null;
    badge_serial: string | null;
    valid_until: string | null;
    /** Zones the organization asked for (per journalist). */
    requested_zones?: ZoneType[] | null;
    /** Zones the league granted, once approved. */
    approved_zones?: ZoneType[] | null;
    // Joined data
    media_personnel?: MediaPersonnel | null;
}

export interface AccreditationDemand {
    id: string;
    organization_id: string;
    match_id: string;
    league_id: string;
    season: string;
    status: DemandStatus;
    message: string | null;
    submitted_by: string | null;
    submitted_at: string;
    reviewed_by: string | null;
    reviewed_at: string | null;
    review_notes: string | null;
    created_at: string;
    updated_at: string;
    /**
     * Reviewer display name, resolved by useAccreditationDemands via a
     * secondary profiles lookup (reviewed_by references auth.users, so no
     * PostgREST embed exists). Null when unreviewed or the profile is gone.
     */
    reviewer_name?: string | null;
    // Joined data
    organization?: Pick<
        MediaOrganization,
        "id" | "name" | "name_ar" | "logo_url" | "account_kind" | "team_id" | "team" | "deleted_at"
    > | null;
    match?: {
        id: string;
        /** matches.status (match_status enum) — decides "played" for deleted holders. */
        status?: string | null;
        match_date: string;
        match_time: string | null;
        game_day: number | null;
        home_team?: { name: string; name_ar: string | null } | null;
        away_team?: { name: string; name_ar: string | null } | null;
        stadium?: MediaMatchStadium | null;
    } | null;
    league?: { name: string; name_ar: string | null } | null;
    requests?: DemandRequestRow[];
}

export interface AccreditationDemandFilters {
    status?: DemandStatus | null;
}

export interface ReviewDemandOverride {
    action: "approve" | "reject";
    rejection_reason?: string;
    /** Per-journalist zones; takes priority over the payload-level approved_zones. */
    approved_zones?: ZoneType[];
}

export interface ReviewDemandPayload {
    demand_id: string;
    default_action: "approve" | "reject";
    approved_zones?: ZoneType[];
    valid_from?: string;
    valid_until?: string;
    rejection_reason?: string;
    review_notes?: string;
    overrides?: Record<string, ReviewDemandOverride>;
}

export interface ReviewDemandResponse {
    success: boolean;
    demand_id: string;
    status: DemandStatus;
    approved_count: number;
    rejected_count: number;
    message: string;
}

// Per-league access (org_league_access): the organization requests it from the
// app, the league grants / rejects / revokes it here (review-league-access).
// Rejected → the org may re-request; revoked → only the league restores it.
export type OrgLeagueAgrementStatus = "pending" | "granted" | "rejected" | "revoked";

export interface OrgLeagueAccess {
    id: string;
    organization_id: string;
    league_id: string;
    agrement_status: OrgLeagueAgrementStatus;
    agreement_url: string | null;
    granted_by: string | null;
    granted_at: string | null;
    notes: string | null;
    /** Reason given with the last reject / revoke (null after a grant). */
    decision_reason?: string | null;
    /** Optional message the organization sent with its request. */
    request_message?: string | null;
    created_at: string;
    updated_at: string;
    /**
     * Name of the reviewer who granted it, resolved by useLeagueAccessRequests
     * via a secondary profiles lookup (granted_by references auth.users).
     */
    granted_by_name?: string | null;
    // Joined data
    league?: { name: string; name_ar: string | null } | null;
    organization?: Pick<
        MediaOrganization,
        | "id"
        | "name"
        | "name_ar"
        | "org_type"
        | "logo_url"
        | "account_kind"
        | "team_id"
        | "team"
        | "is_approved"
        | "deleted_at"
    > | null;
}

export interface LeagueAccessFilters {
    status?: OrgLeagueAgrementStatus | null;
}

export interface ReviewLeagueAccessPayload {
    access_id: string;
    action: "grant" | "reject" | "revoke";
    reason?: string;
}

export interface ReviewLeagueAccessResponse {
    success: boolean;
    access_id: string;
    status: OrgLeagueAgrementStatus;
}

export interface MediaAccessLog {
    id: string;
    accreditation_id: string | null;
    token_id: string | null;
    match_id: string | null;
    scanner_id: string | null;
    scanner_device_id: string | null;
    scan_result: ScanResultType;
    failure_reason: string | null;
    zone_accessed: ZoneType | null;
    gate_location: string | null;
    gps_latitude: number | null;
    gps_longitude: number | null;
    scanned_at: string;
    scan_data: Record<string, unknown> | null;
    // Joined data
    accreditation?: AccreditationRequest;
    match?: {
        id: string;
        match_date: string;
        match_time?: string | null;
        game_day?: number | null;
        league_group?: { league_id: string | null } | null;
        home_team: { name: string; name_ar?: string | null };
        away_team: { name: string; name_ar?: string | null };
    } | null;
    /** Gate agent who performed the scan (null for anonymous/public scans). */
    scanner?: { id: string; full_name: string | null } | null;
}

// ============================================================================
// BADGE TEMPLATES
// ============================================================================

/**
 * Visual + textual configuration for a league accreditation badge.
 * Consumed by the generate-accreditation-badge edge function to compose the PDF.
 */
export interface BadgeTemplateConfig {
    primary_color?: string;
    secondary_color?: string;
    accent_color?: string;
    text_color?: string;
    background_color?: string;
    /** Printed in the notice box on the back; empty → default FR + AR notice. */
    footer_text?: string;
    logo_url?: string | null;
    background_image_url?: string | null;
    show_photo?: boolean;
    show_qr?: boolean;
    show_zones?: boolean;
    show_organization?: boolean;
    show_match?: boolean;
    show_validity?: boolean;
    contact_website?: string;
    contact_email?: string;
    contact_facebook?: string;
    terms_fr?: string;
    terms_ar?: string;
}

export interface BadgeTemplate {
    id: string;
    league_id: string;
    season: string | null;
    name: string;
    is_default: boolean;
    is_active: boolean;
    config: BadgeTemplateConfig;
    created_at?: string;
    updated_at?: string | null;
    created_by?: string | null;
    updated_by?: string | null;
}

export interface UpsertBadgeTemplatePayload {
    id?: string;
    league_id: string;
    season?: string | null;
    name: string;
    is_default?: boolean;
    is_active?: boolean;
    config: BadgeTemplateConfig;
}

export interface GenerateBadgeResponse {
    success: boolean;
    badge_serial: string;
    badge_issued_at: string;
    pdf_url: string;
    expires_in: number;
    verification_url: string;
}

// ============================================================================
// API TYPES - Edge function request/response types
// ============================================================================

export interface ScanAccreditationRequest {
    token: string;
    /** The match being controlled — makes a wrong-match badge a refusal, and lets a delegate be recognised. */
    match_id?: string;
    zone_type?: ZoneType;
    /** Client checkpoint: 'webapp_gate' (scan mode), 'delegate_app' (officials app)… Omitted by /media-verify. */
    gate_location?: string;
    /** Stable per-browser id of the scanning device (media_access_logs.scanner_device_id). */
    device_id?: string;
}

// `scan-accreditation` returns a TIERED payload (see
// supabase/functions/MEDIA_FUNCTIONS.md). Only holder_name and organization are
// guaranteed: the public /media-verify page calls the function anonymously and
// gets nothing else. Everything below them is present ONLY for a recognized
// scanner (security_gate / super_admin / league_admin / media_manager, or the
// delegate of the scanned match), so every one of those fields must be guarded
// at the use site.
//
// Contract v2 (docs/superpowers/plans/2026-10-04-media-qr-scan-experience.md)
// only ADDS fields — recorded_as, timing, match, valid_from, valid_until,
// previous_entry, accreditation.qr_kind / holder_name_ar. A server that
// predates it sends none of them, so they are all optional here and every
// reader must degrade to the v1 behaviour when they are absent.

/** Algiers day of "now" vs the accreditation's match day. */
export type ScanTiming = "before_match_day" | "match_day" | "after_match";

/** How scan-accreditation journalised the scan. */
export type ScanRecordedAs = "entry" | "verification" | "automated";

/** Printed badge vs the dynamic QR of the media app. */
export type ScanQrKind = "badge" | "mobile_qr";

export interface ScanMatchSummary {
    id: string;
    /** YYYY-MM-DD, Algiers. */
    date: string | null;
    /** HH:MM, Algiers. */
    time: string | null;
    home: { name: string | null; name_ar: string | null };
    away: { name: string | null; name_ar: string | null };
    stadium: { name: string | null; name_ar: string | null } | null;
}

export interface ScanAccreditationResponse {
    valid: boolean;
    error?: string;
    scan_result?: string;
    message?: string;
    // ---- v2 (optional: absent on a v1 server) ----
    recorded_as?: ScanRecordedAs;
    timing?: ScanTiming | null;
    match?: ScanMatchSummary | null;
    valid_from?: string | null;
    valid_until?: string | null;
    /** Full tier only: first ENTRY of this accreditation on the match day, before this scan. */
    previous_entry?: { scanned_at: string; gate_location: string | null } | null;
    accreditation?: {
        holder_name: string;
        organization: string;
        /** v2, when the server knows the Arabic name. */
        holder_name_ar?: string | null;
        // Authorized-scanner tier only:
        id?: string;
        /** Signed URL (2 h) — the field scan-accreditation actually sends. */
        photo_url?: string | null;
        /** Legacy name, never sent by the current function. */
        holder_photo?: string;
        media_type?: MediaPersonnelType;
        approved_zones?: ZoneType[];
        valid_until?: string | null;
        match_id?: string | null;
        /** v2: printed badge vs dynamic QR of the app. */
        qr_kind?: ScanQrKind;
    };
}

export interface ReviewAccreditationPayload {
    request_id: string;
    action: "approve" | "reject" | "request_info";
    approved_zones?: ZoneType[];
    valid_from?: string;
    valid_until?: string;
    rejection_reason?: string;
}

// ============================================================================
// UI HELPER TYPES
// ============================================================================

export interface MediaPersonnelFilters {
    isVerified?: boolean | null; // null = all, true = verified, false = pending/rejected
    mediaType?: MediaPersonnelType | null;
    organizationId?: string | null;
    searchQuery?: string;
}

export interface AccreditationRequestFilters {
    status?: AccreditationStatus | null;
    requestType?: AccreditationRequestType | null;
    leagueId?: string | null;
    season?: string | null;
    dateFrom?: string | null;
    dateTo?: string | null;
}

export interface MediaAccessLogFilters {
    matchId?: string | null;
    scanResult?: ScanResultType | null;
    zoneType?: ZoneType | null;
    dateFrom?: string | null;
    dateTo?: string | null;
}

// ── Vérification des documents ────────────────────────────────────────────

export type DocumentCheckStatus =
    | "concordant"
    | "ecart_mineur"
    | "ecart_bloquant"
    | "illisible";

export type DocumentFindingVerdict =
    | "match"
    | "mismatch"
    | "absent_du_document"
    | "absent_de_la_fiche";

export interface DocumentFinding {
    field: string;
    verdict: DocumentFindingVerdict;
    fiche: string | null;
    document: string | null;
    explanation: string;
    /**
     * Nature d'un écart d'IDENTITÉ, quand elle change la gravité.
     *   `identite`    — l'écart met en cause l'appartenance du document ;
     *   `saisie`      — naissance à un jour d'écart (décalage LIRF connu) ;
     *   `orthographe` — nom divergent dans UNE écriture alors que l'AUTRE
     *                   corrobore l'identité.
     * Absent sur tout champ non identitaire et sur les lignes écrites avant
     * le recalibrage du 2026-08-22.
     */
    severity?: "identite" | "saisie" | "orthographe";
    /** Nature d'une concordance de nom. Informatif à l'écran. */
    matchQuality?: "exacte" | "agglutination" | "translitteration" | "inclusion";
    /**
     * Concordance obtenue par inclusion d'un document portant MOINS de jetons
     * que la fiche : lecture tronquée. Elle ne corrobore pas l'identité.
     */
    documentTronque?: boolean;
}

export interface DocumentPhotoMatch {
    same_person: boolean;
    confidence: number;
    note: string;
}

/** Une valeur lue sur le document, avec la confiance du modèle. */
export interface ExtractedFieldValue {
    value: string | null;
    confidence: number;
}

/**
 * Une entrée d'`extracted`.
 *
 * La fonction y écrit DEUX formes, et le type doit les dire toutes les deux :
 * les champs lus (`card_number`, `name_latin`…) sous la forme
 * `{ value, confidence }`, et des clés RÉSERVÉES portant un motif en clair,
 * en chaîne nue — `_document_non_carte` (le document n'est pas une carte de
 * presse), `_echec_lecture` (réponse du modèle inexploitable),
 * `_document_refuse` (format inconnu, taille excessive). La lecture du tiroir
 * est défensive et s'en accommodait, mais un type qui ment finit par tromper
 * quelqu'un.
 */
export type ExtractedEntry = ExtractedFieldValue | string;

export interface MediaDocumentCheck {
    id: string;
    personnel_id: string;
    document_kind: string;
    document_path: string;
    document_hash: string;
    model: string;
    extracted: Record<string, ExtractedEntry>;
    findings: DocumentFinding[];
    overall_status: DocumentCheckStatus;
    photo_match: DocumentPhotoMatch | null;
    reviewed_by: string | null;
    reviewed_at: string | null;
    review_action: "applique" | "rejete" | "ignore" | null;
    applied_fields: Record<string, string> | null;
    created_at: string;
}

/**
 * Ce qu'affiche la colonne « Document ».
 *
 * L'absence de ligne est un état à part entière (`non_verifie`), et
 * `ecart_mineur` / `ecart_bloquant` restent DISTINCTS jusqu'à la pastille et
 * jusqu'au filtre : c'est le seul tri bon marché dont dispose
 * l'administrateur devant 240 fiches, et un écart d'organe de presse n'est
 * pas un écart d'identité.
 */
export type DocumentCellStatus =
    | "non_verifie"
    | "concordant"
    | "ecart_mineur"
    | "ecart_bloquant"
    | "illisible";

/**
 * Gravité décroissante, pour le tri de la colonne. Le tri alphabétique des
 * codes n'aurait aucun sens : ce qui appelle une décision doit remonter.
 */
export const DOCUMENT_CELL_SEVERITY: Record<DocumentCellStatus, number> = {
    ecart_bloquant: 0,
    illisible: 1,
    ecart_mineur: 2,
    non_verifie: 3,
    concordant: 4,
};
